import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { isFeatureEnabledForActiveModule } from 'src/helpers/module_helper';
import useMailerDriver from 'src/modules/mailer/hooks/useMailerDriver';
import useMailerSettingsService from 'src/modules/mailer/hooks/useMailerSettingsService';
import useMailboxRoleAccess from 'src/modules/mailer/hooks/useMailboxRoleAccess';

const useMailboxGuard = ({
    t,
}) => {
    const history = useHistory();
    const redirectedRef = useRef(false);
    const mailerEnabled = isFeatureEnabledForActiveModule('mailer');
    const { active, settings } = useMailerDriver();
    const service = useMailerSettingsService();
    const { isRestrictedAnalyst } = useMailboxRoleAccess();

    const {
        data,
        isLoading,
        isError,
    } = useQuery({
        queryKey: [settings.settingsQueryKey, active],
        queryFn: async () => {
            return await service.fetchMailerSettings();
        },
        enabled: mailerEnabled && !isRestrictedAnalyst,
        cacheTime: 0,
        refetchOnWindowFocus: false,
    });

    const isChecking = mailerEnabled && isLoading && !isRestrictedAnalyst;
    const hasActiveSettings = !!data?.isActive;
    const shouldRedirect = mailerEnabled
        && !isRestrictedAnalyst
        && !isChecking
        && (isError || !hasActiveSettings);
    const shouldRenderMailbox = mailerEnabled && !isChecking && (
        isRestrictedAnalyst || (!isError && hasActiveSettings)
    );

    useEffect(() => {
        redirectedRef.current = false;
    }, [active, settings.settingsPath]);

    useEffect(() => {
        if (!shouldRedirect || redirectedRef.current) {
            return;
        }

        redirectedRef.current = true;
        toast.info(t('Active mailer settings are required for mailbox'));
        history.replace(settings.settingsPath);
    }, [shouldRedirect, history, t, settings.settingsPath]);

    return {
        mailerEnabled,
        isChecking,
        shouldRenderMailbox,
    };
};

export default useMailboxGuard;
